import { useQuery } from '@tanstack/react-query'
import { publicApiClient } from '@/api/public'
import { DATA_PROCESSING_TERMS, PHOTO_GUIDE, type Block, type LegalDoc, type Section } from './legalContent'
import { parseLegalMarkup } from './legalMarkup'

const isUrl = (s: string) => /^https?:\/\//.test(s.split(': ').pop() ?? '')

function renderItem(text: string) {
  const idx = text.indexOf('https://')
  if (idx === -1 || !isUrl(text)) return text
  const url = text.slice(idx)
  return (
    <>
      {text.slice(0, idx)}
      <a href={url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800 underline break-all">{url}</a>
    </>
  )
}

function BlockView({ block }: { block: Block }) {
  switch (block.type) {
    case 'p':
      return <p className="text-sm text-gray-700 leading-relaxed">{block.text}</p>
    case 'note':
      return <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">{block.text}</p>
    case 'ul':
      return <ul className="list-disc pl-5 space-y-1.5 text-sm text-gray-700 leading-relaxed">{block.items.map((i, k) => <li key={k}>{renderItem(i)}</li>)}</ul>
    case 'ol':
      return <ol className="list-decimal pl-5 space-y-1.5 text-sm text-gray-700 leading-relaxed">{block.items.map((i, k) => <li key={k}>{renderItem(i)}</li>)}</ol>
    case 'table':
      return (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>{block.headers.map(h => <th key={h} className="text-left px-3 py-2 font-semibold text-gray-600">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {block.rows.map((r, i) => (
                <tr key={i} className="align-top">
                  {r.map((c, j) => <td key={j} className={`px-3 py-2 text-gray-700 ${j === 0 ? 'font-medium whitespace-nowrap' : ''}`}>{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
  }
}

/** The document text (introduction and numbered sections). Also used for the SuperAdmin's live preview. */
export function LegalDocumentBody({ intro, sections }: { intro: Block[]; sections: Section[] }) {
  return (
    <>
      <div className="mt-6 space-y-3">{intro.map((b, i) => <BlockView key={i} block={b} />)}</div>

      {sections.map((s, n) => (
        <section key={n} className="mt-8 space-y-3">
          <h2 className="text-base font-semibold text-gray-900">{s.heading}</h2>
          {s.blocks.map((b, i) => <BlockView key={i} block={b} />)}
        </section>
      ))}
    </>
  )
}

export function LegalDocumentHeader({ title, subtitle, meta }: { title: string; subtitle?: string | null; meta?: string }) {
  return (
    <>
      <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      {meta && <p className="text-xs text-gray-400 mt-1">{meta}</p>}
    </>
  )
}

const formatDay = (d: string) =>
  new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

/** Shows the SuperAdmin's edited text if there is any, otherwise the built-in default. */
function LegalDocumentView({ docKey, fallback }: { docKey: string; fallback: LegalDoc }) {
  const { data: edited, isLoading } = useQuery({
    queryKey: ['public-legal-doc', docKey],
    queryFn: () => publicApiClient.getLegalDocument(docKey),
    staleTime: 60 * 1000,
  })

  const doc: LegalDoc = edited
    ? {
        title: edited.title,
        subtitle: edited.subtitle ?? undefined,
        meta: `Version ${edited.version} · effective ${formatDay(edited.effectiveOn)}`,
        ...parseLegalMarkup(edited.body),
      }
    : fallback

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-5 py-10">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-6">CertSlate</p>
        {isLoading ? <p className="text-sm text-gray-400">Loading…</p> : (
          <>
            <LegalDocumentHeader title={doc.title} subtitle={doc.subtitle} meta={doc.meta} />
            <LegalDocumentBody intro={doc.intro} sections={doc.sections} />
          </>
        )}
      </div>
    </div>
  )
}

export function DataProcessingTermsPage() {
  return <LegalDocumentView docKey="data-processing" fallback={DATA_PROCESSING_TERMS} />
}

export function PhotoGuidePage() {
  return <LegalDocumentView docKey="photo-guide" fallback={PHOTO_GUIDE} />
}
